import { useEffect, useState, useCallback } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { MotiView } from "moti";
import { useGetCourseByIdQuery, useGetFacultyByCourseIdQuery } from "../graphql/generated/graphql";
import CustomProgressBar from "../components/common/general/CustomProgressBar/CustomProgressBar";
import CoreTitle from "../components/svgs/common/CoreTitle";
import JourneyBreadcrumbs from "../components/journey/JourneyBreadcrumbs/JourneyBreadcrumbs";
import Skeleton from "react-loading-skeleton";
import NoChatBarLayout from "../layouts/NoChatBarLayout.web";
import AiTutorCardProgressListContainer from "../containers/school/AiTutorCardProgressListContainer/AiTutorCardProgressListContainer";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import { shadowStyles } from "../styles/shadow";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import useResponsiveScreen from "../hooks/useResponsiveScreen";

function CustomCourseStudentView() {
  const posthog = usePostHog();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course Student">>();
  const { isMedium } = useResponsiveScreen();

  const { course_id: courseId, integration_id: integrationId } = route.params;

  const [courseProgress, setCourseProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState("");

  const { data: course, refetch } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const { data: facultyData } = useGetFacultyByCourseIdQuery({ variables: { courseId } });
  const hasFaculty = facultyData?.getFacultyByCourseId?.data?.faculties.length > 0;

  useEffect(() => {
    posthog?.capture("School Page Visited");
  }, []);

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""}${remainingMinutes > 0 ? ` ${remainingMinutes} min` : ""}`;
    } else {
      return `${minutes} min`;
    }
  };

  const handleFacultyViewPress = () => {
    navigation.navigate("Faculty", {
      courseName: course?.getCourseById.data[0].name,
      courseId: courseId,
      integration_id: integrationId,
    });
  };

  const handleProgressUpdate = useCallback(
    (progress, durationMinutes) => {
      setCourseProgress(progress);
      setTimeLeft(formatDuration(durationMinutes));
    },
    [formatDuration]
  );

  const handleBackPress = () => {
    navigation.navigate("Custom School Student", {
      integration_id: integrationId,
    });
  };

  return (
    <NoChatBarLayout>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={800}
        // @ts-expect-error this is a valid transition type
        transition={{ type: "timing", duration: 600 }}>
        <View style={styles.headerContainer}>
          <JourneyBreadcrumbs
            onPress={handleBackPress}
            firstItem="Courses"
            secondItem={course?.getCourseById.data[0].name}
          />
          <View
            style={{
              flexDirection: isMedium ? "row" : "column",
              flexWrap: isMedium ? "wrap" : "nowrap",
              justifyContent: isMedium ? "space-between" : "flex-start",
              alignItems: isMedium ? "center" : "flex-start",
              width: "100%",
              gap: 12,
            }}>
            <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", flex: 1 }}>
              <CoreTitle />
              <CustomText
                useTranslationText={false}
                text={course?.getCourseById.data[0].name}
                size="xxl"
                style={{
                  fontFamily: "AbrilFatface",
                  fontSize: 46,
                  lineHeight: 50,
                  fontWeight: 800,
                  marginLeft: 8,
                  flexShrink: 1,
                  flexGrow: 1,
                  flexBasis: "auto",
                }}
              />
            </View>

            {hasFaculty && (
              <CustomButton
                text={"Course Instructor"}
                styleType="primaryLight"
                onPress={handleFacultyViewPress}
                leftIcon={null}
                size="s"
                aria-label="Course Instructor"
                style={shadowStyles.smallShadowButton}
              />
            )}
          </View>
          <CustomSpacing size="l" />
        </View>

        {timeLeft !== "" ? (
          <View style={[styles.courseProgress, { flex: 1, flexDirection: "row", alignItems: "center", gap: 12 }]}>
            <View style={{ maxWidth: 200, flex: 1 }}>
              <CustomProgressBar progress={courseProgress} color="#3E68FE" ariaLabel="Course Progress" />
            </View>
            <CustomText
              text={`${Math.round(courseProgress)}% Complete`}
              useTranslationText={false}
              textType="text"
              weight="normal"
              size="xs"
            />
          </View>
        ) : (
          <View style={[styles.courseProgress, { flex: 1, flexDirection: "row", alignItems: "center", gap: 16 }]}>
            <View style={{ maxWidth: 200, flex: 1 }}>
              <Skeleton height={8} width="100%" />
            </View>
          </View>
        )}
      </MotiView>
      <AiTutorCardProgressListContainer
        integrationId={integrationId}
        courseId={courseId}
        onProgressUpdate={handleProgressUpdate}
      />
    </NoChatBarLayout>
  );
}

const styles = StyleSheet.create({
  headerTitleRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
  },
  headerContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  courseProgress: {
    marginVertical: 12,
  },
  progressInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  timeText: {
    color: "#667085",
  },
});

export default CustomCourseStudentView;
